/**
 * GSAP Animation Utilities for Success Club
 * Reusable animation functions for consistent, smooth animations
 */

import { gsap } from 'gsap';

/**
 * Fade in animation for elements
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const fadeIn = (target, options = {}) => {
  const defaults = {
    opacity: 0,
    y: 20,
    duration: 0.6,
    ease: 'power2.out',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Fade out animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const fadeOut = (target, options = {}) => {
  const defaults = {
    opacity: 0,
    duration: 0.4,
    ease: 'power2.in',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Slide in from left animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const slideInLeft = (target, options = {}) => {
  const defaults = {
    x: -50,
    opacity: 0,
    duration: 0.6,
    ease: 'power3.out',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Slide in from right animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const slideInRight = (target, options = {}) => {
  const defaults = {
    x: 50,
    opacity: 0,
    duration: 0.6,
    ease: 'power3.out',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Scale up animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const scaleIn = (target, options = {}) => {
  const defaults = {
    scale: 0.8,
    opacity: 0,
    duration: 0.5,
    ease: 'back.out(1.7)',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Stagger animation for multiple elements
 * @param {string|Element} targets - Elements or selector to animate
 * @param {object} options - Animation options
 */
export const staggerFadeIn = (targets, options = {}) => {
  const defaults = {
    opacity: 0,
    y: 30,
    duration: 0.6,
    stagger: 0.1,
    ease: 'power2.out',
    ...options
  };

  return gsap.from(targets, defaults);
};

/**
 * Counter animation for numbers
 * @param {Element} element - Element containing the number
 * @param {number} endValue - Target number
 * @param {object} options - Animation options
 */
export const animateCounter = (element, endValue, options = {}) => {
  const defaults = {
    duration: 1.5,
    ease: 'power2.out',
    ...options
  };

  const obj = { value: 0 };
  
  return gsap.to(obj, {
    value: endValue,
    ...defaults,
    onUpdate: () => {
      element.textContent = Math.floor(obj.value).toLocaleString();
    }
  });
};

/**
 * Pulse animation for attention
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const pulse = (target, options = {}) => {
  const defaults = {
    scale: 1.05,
    duration: 0.5,
    yoyo: true,
    repeat: 3,
    ease: 'power1.inOut',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Modal appearance animation
 * @param {string|Element} target - Modal element or selector
 * @param {object} options - Animation options
 */
export const modalAppear = (target, options = {}) => {
  const defaults = {
    scale: 0.9,
    opacity: 0,
    duration: 0.3,
    ease: 'power2.out',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Modal disappear animation
 * @param {string|Element} target - Modal element or selector
 * @param {object} options - Animation options
 */
export const modalDisappear = (target, options = {}) => {
  const defaults = {
    scale: 0.9,
    opacity: 0,
    duration: 0.2,
    ease: 'power2.in',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Card flip animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const flipCard = (target, options = {}) => {
  const defaults = {
    rotationY: 180,
    duration: 0.6,
    ease: 'power2.inOut',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Bounce animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const bounce = (target, options = {}) => {
  const defaults = {
    y: -20,
    duration: 0.5,
    yoyo: true,
    repeat: 1,
    ease: 'power1.inOut',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Shake animation for errors
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const shake = (target, options = {}) => {
  const defaults = {
    x: [-10, 10, -10, 10, -5, 5, 0],
    duration: 0.5,
    ease: 'power1.inOut',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Rotate animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const rotate = (target, options = {}) => {
  const defaults = {
    rotation: 360,
    duration: 0.6,
    ease: 'power2.out',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Progress bar fill animation
 * @param {string|Element} target - Progress bar element
 * @param {number} percentage - Target percentage (0-100)
 * @param {object} options - Animation options
 */
export const animateProgressBar = (target, percentage, options = {}) => {
  const defaults = {
    width: `${percentage}%`,
    duration: 1,
    ease: 'power2.out',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Typewriter text animation
 * @param {Element} element - Text element
 * @param {string} text - Text to type
 * @param {object} options - Animation options
 */
export const typewriter = (element, text, options = {}) => {
  const defaults = {
    duration: text.length * 0.05,
    ease: 'none',
    ...options
  };

  element.textContent = '';
  const chars = text.split('');
  
  return gsap.to({}, {
    ...defaults,
    onUpdate: function() {
      const progress = this.progress();
      const currentLength = Math.floor(chars.length * progress);
      element.textContent = chars.slice(0, currentLength).join('');
    }
  });
};

/**
 * Glow effect animation
 * @param {string|Element} target - Element or selector to animate
 * @param {object} options - Animation options
 */
export const glow = (target, options = {}) => {
  const defaults = {
    boxShadow: '0 0 20px rgba(212, 175, 55, 0.8)',
    duration: 0.8,
    yoyo: true,
    repeat: -1,
    ease: 'power1.inOut',
    ...options
  };

  return gsap.to(target, defaults);
};

/**
 * Page transition animation
 * @param {string|Element} target - Page container element
 * @param {object} options - Animation options
 */
export const pageTransition = (target, options = {}) => {
  const defaults = {
    opacity: 0,
    x: 50,
    duration: 0.5,
    ease: 'power2.out',
    ...options
  };

  return gsap.from(target, defaults);
};

/**
 * Button hover effect
 * @param {string|Element} target - Button element
 * @param {boolean} isHover - Whether mouse is hovering
 */
export const buttonHover = (target, isHover) => {
  return gsap.to(target, {
    scale: isHover ? 1.05 : 1,
    duration: 0.3,
    ease: 'power2.out'
  });
};

/**
 * Notification slide in
 * @param {string|Element} target - Notification element
 * @param {object} options - Animation options
 */
export const notificationSlideIn = (target, options = {}) => {
  const defaults = {
    x: 100,
    opacity: 0,
    duration: 0.4,
    ease: 'power2.out',
    ...options
  };

  return gsap.from(target, defaults);
};
